import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DataTable } from "@/components/DataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { Fine, FineSummary, ColumnFilterOption } from "@/types";
import { formatGridDate } from "@/lib/utils";

export default function FineRegisterPage() {
  const { api, user } = useAuth();
  const [fines, setFines] = useState<Fine[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<FineSummary | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  const filterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAdmin = user?.role === "admin" || user?.role === "librarian";

  const fetchFines = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 20 };
      const textParts = [columnFilters.student_name, columnFilters.student_admission_no].filter(Boolean);
      if (textParts.length) params.search = textParts.join(" ");
      if (columnFilters.paid === "paid") params.paid = true;
      else if (columnFilters.paid === "unpaid") params.paid = false;
      const [res, sum] = await Promise.all([api.get("/fines", { params }), api.get("/fines/summary")]);
      setFines(res.data.items);
      setTotalPages(res.data.pages);
      setSummary(sum.data);
    } catch {
      toast.error("Failed to load fines");
    } finally {
      setLoading(false);
    }
  }, [api, page, columnFilters]);

  useEffect(() => {
    fetchFines();
  }, [fetchFines]);

  const handleColumnFiltersChange = useCallback((filters: Record<string, any>) => {
    setColumnFilters(filters);
    if (filterTimer.current) clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => setPage(1), 400);
  }, []);

  const payFine = async (fineId: string) => {
    try {
      await api.post(`/fines/${fineId}/pay`);
      toast.success("Fine marked as paid");
      fetchFines();
    } catch (err) {
      toast.error("Failed to pay fine");
    }
  };

  const deleteFine = async (fine: Fine) => {
    if (!window.confirm(`Delete ₹${fine.amount} fine for "${fine.student_name}"?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/fines/${fine.id}`);
      toast.success("Fine deleted");
      fetchFines();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to delete fine");
    }
  };

  const paidOptions: ColumnFilterOption[] = [
    { label: "Paid", value: "paid" },
    { label: "Unpaid", value: "unpaid" },
  ];

  const columns = [
    { accessorKey: "student_name", header: "Student", meta: { filterVariant: "text", filterPlaceholder: "Student..." } },
    {
      accessorKey: "student_admission_no",
      header: "Adm No",
      meta: { filterVariant: "text", filterPlaceholder: "Adm No...", hideOnMobile: true },
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.student_admission_no}</span>,
    },
    { accessorKey: "amount", header: "Amount", cell: ({ row }) => <span className="font-semibold">₹{row.original.amount}</span> },
    {
      accessorKey: "paid",
      header: "Status",
      meta: { filterVariant: "select", filterOptions: paidOptions, filterPlaceholder: "Status" },
      cell: ({ row }) => (
        <Badge className={row.original.paid ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-red-100 text-red-800 hover:bg-red-100"}>
          {row.original.paid ? "Paid" : "Unpaid"}
        </Badge>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Date",
      meta: { hideOnMobile: true },
      cell: ({ row }) => formatGridDate(row.original.created_at),
    },
    ...(isAdmin
      ? [
          {
            id: "actions",
            header: "Actions",
            cell: ({ row }) => (
              <div className="flex items-center gap-1">
                {!row.original.paid && (
                  <Button
                    size="sm"
                    className="h-7 bg-primary hover:bg-primary/90"
                    onClick={() => payFine(row.original.id)}
                    data-testid={`pay-fine-${row.original.id}`}
                  >
                    Mark Paid
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive"
                  onClick={() => deleteFine(row.original)}
                  title="Delete fine"
                  data-testid={`delete-fine-${row.original.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="fine-register-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Fine Register</h1>
        <p className="mt-1 text-sm text-muted-foreground sm:text-base">Track and manage fines</p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total Fines", value: summary.total_fines },
            { label: "Total Amount", value: `₹${summary.total_amount}` },
            { label: "Collected", value: `₹${summary.paid_amount}` },
            { label: "Pending", value: `₹${summary.unpaid_amount}` },
          ].map((c) => (
            <div key={c.label} className="nx-surface rounded-xl border border-border bg-card p-4 text-card-foreground">
              <p className="text-2xl font-bold text-foreground">{c.value}</p>
              <p className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">{c.label}</p>
            </div>
          ))}
        </div>
      )}

      <DataTable
        columns={columns}
        data={fines}
        totalPages={totalPages}
        currentPage={page}
        onPageChange={setPage}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        loading={loading}
        data-testid="fines-table"
      />
    </div>
  );
}
